import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { FotoSerit } from '../lib/foto-goster';

/**
 * MÜŞTERİ İÇİN İŞ DETAYI — SALT OKUNUR (07.09.2026).
 *
 * ⚠️ NEDEN AYRI EKRAN, `IsDetay`'a bayrak KOYMAK DEĞİL: masaüstü iş detayı
 *    1400 satır ve her adımda eylem düğmesi taşıyor (foto ekle, izin, ekip,
 *    onayla & teslim, düzeltme "⋮", geri al, ertele, iptal…). Oraya bir
 *    `saltOkunur` bayrağı serpiştirmek, ileride eklenecek HER yeni düğmenin
 *    bayrağı unutması riskini kalıcı hale getirirdi. Ayrı ekran, yapısı
 *    gereği okunur: içinde bir eylem ucu yok.
 *
 * ⚠️ İZ KAYDI BİLİNÇLİ OLARAK YOK. Zaman çizgisi Mod'un iç süreci: "geri
 *    alındı (yanlış tik)", "şef düzeltmesi", temsil kayıtları. Müşteriye
 *    gösterilecek kanıt fotoğraflar, ölçüler ve teslim tarihidir.
 *
 * Sunucu bu ekrana özel bir uç nokta istemiyor: `/isler/:id` zaten şantiye
 * kapsamıyla (RLS) sınırlı ve müşteri kendi şantiyesini görüyor.
 */
interface Detay {
  id: string;
  isEmriNo: string | null;
  iskeleOnayNo: string | null;
  /** SAP iş emri alanları — yalnız SOCAR gibi müşterilerde dolu. */
  sorumluIsyeri?: string | null;
  planlamaGrubu?: string | null;
  oncelik?: number | null;
  tanim: string;
  unite: string;
  durum: string;
  ilgili: string[];
  metrajM3: number | null;
  metrajM2: number | null;
  teslimAt: string | null;
  kuruluGun: number | null;
  yesilKart: boolean;
  kesifNot: string | null;
  olculer: { ad: string | null; en: number | null; boy: number | null; yuk: number | null }[];
  fotolar: { fotoNo: number; step: string; cekilmeZamani: string; contentType?: string | null; gecersiz?: boolean }[];
}

const DURUM_ETIKET: Record<string, string> = {
  giris: 'Keşif bekliyor',
  kesif: 'İzin & malzeme bekleniyor',
  hazirlik: 'Kuruluma hazır',
  kurulum: 'Kurulum sürüyor',
  kontrol: 'Kontrol & onay bekliyor',
  ayakta: 'Kurulu',
  revize: 'Revizyonda',
  sokum: 'Sökümde',
  kapali: 'Söküldü',
  ertelendi: 'Ertelendi',
  iptal: 'İptal',
};

/** Kanıt şeridinin başlıkları — müşteri hangi belgeye baktığını bilmeli. */
const ADIM_ETIKET: Record<string, string> = {
  is_emri: 'İş emri kâğıdı',
  kesif: 'Keşif',
  izin: 'İş izni',
  kurulum: 'Kurulum',
  kontrol_formu: 'Kontrol formu',
  yesil_kart: 'Yeşil kart',
  kurulu_iskele: 'Kurulu iskele',
  rutin_kontrol: 'Haftalık kontrol',
  revize: 'Revizyon',
  sokum_is_emri: 'Söküm iş emri',
  sokum: 'Söküm',
  bos_alan: 'Alan temizliği',
};

const say = (n: number | null) =>
  n == null ? '—' : n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function MusteriIsDetay({
  id,
  onGeri,
  geriEtiket,
}: {
  id: string;
  onGeri: () => void;
  geriEtiket: string;
}) {
  const [d, setD] = useState<Detay | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Detay>(`/isler/${id}`)
      .then(setD)
      .catch((e) => setHata(e instanceof Error ? e.message : 'Yüklenemedi'));
  }, [id]);

  if (hata) return <p className="m-6 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;
  if (!d) return <p className="m-6 text-sm text-wait">Yükleniyor…</p>;

  // Geçersiz işaretlenen kayıt müşteriye giden hiçbir yerde görünmez (K8 kararı).
  const fotolar = d.fotolar.filter((f) => !f.gecersiz);
  const adimlar = [...new Set(fotolar.map((f) => f.step))];

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <button onClick={onGeri} className="mb-3 text-sm text-muted hover:text-navy">
        ← {geriEtiket}
      </button>

      <div className="rounded-(--radius-kart) border border-line bg-surface p-5 shadow-(--shadow-mod)">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-ink">{d.tanim}</h1>
            <p className="mt-0.5 text-sm text-wait">
              {d.unite}
              {d.ilgili.length > 0 && <> · İlgili: {d.ilgili.join(', ')}</>}
            </p>
          </div>
          <span className="rozet border-line-2 bg-surface-2 text-xs text-ink-2">
            {DURUM_ETIKET[d.durum] ?? d.durum}
          </span>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-wait">İş emri no</dt>
            <dd className="num text-ink">{d.isEmriNo ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-wait">İskele onay no</dt>
            <dd className="num text-ink">{d.iskeleOnayNo ?? '—'}</dd>
          </div>
          {(d.sorumluIsyeri || d.planlamaGrubu || d.oncelik) && (
            <>
              <div>
                <dt className="text-xs text-wait">Öncelik</dt>
                <dd className="num text-ink">{d.oncelik ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-wait">Sorumlu işyeri</dt>
                <dd className="num text-ink">{d.sorumluIsyeri ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-wait">Planlama grubu</dt>
                <dd className="num text-ink">{d.planlamaGrubu ?? '—'}</dd>
              </div>
            </>
          )}
          <div>
            <dt className="text-xs text-wait">Metraj</dt>
            <dd className="num text-ink">
              {d.metrajM3 != null && d.metrajM3 > 0 ? `${say(d.metrajM3)} m³` : ''}
              {d.metrajM2 != null && d.metrajM2 > 0 ? ` ${say(d.metrajM2)} m²` : ''}
              {!d.metrajM3 && !d.metrajM2 ? '—' : ''}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-wait">Teslim</dt>
            <dd className="num text-ink">
              {d.teslimAt ? new Date(d.teslimAt).toLocaleDateString('tr-TR') : '—'}
              {d.kuruluGun != null && (
                <span className="text-wait"> · {d.kuruluGun} gün</span>
              )}
            </dd>
          </div>
        </dl>

        {d.olculer.length > 0 && (
          <div className="mt-4">
            <h2 className="mb-1 text-xs font-medium text-wait">Ölçüler</h2>
            <ul className="num space-y-0.5 text-sm text-ink-2">
              {d.olculer.map((o, i) => (
                <li key={i}>
                  {o.ad ? `${o.ad}: ` : ''}
                  {o.en ?? '—'} × {o.boy ?? '—'}
                  {o.yuk ? ` × ${o.yuk}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        {d.kesifNot && (
          <div className="mt-4">
            <h2 className="mb-1 text-xs font-medium text-wait">Keşif notu</h2>
            <p className="text-sm leading-relaxed text-ink-2">{d.kesifNot}</p>
          </div>
        )}
      </div>

      {/* KANIT — adım adım fotoğraflar. Müşterinin bu ekranda asıl aradığı şey. */}
      <section className="mt-4 rounded-(--radius-kart) border border-line bg-surface p-5 shadow-(--shadow-mod)">
        <h2 className="mb-3 text-sm font-semibold text-ink">Fotoğraflar</h2>
        {adimlar.length === 0 ? (
          <p className="text-sm text-wait">Bu işe henüz fotoğraf yüklenmedi.</p>
        ) : (
          <div className="space-y-4">
            {adimlar.map((step) => (
              <div key={step}>
                <h3 className="mb-1.5 text-xs font-medium text-wait">
                  {ADIM_ETIKET[step] ?? step}
                </h3>
                <FotoSerit fotolar={fotolar.filter((f) => f.step === step)} boyut={72} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
